function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

// document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
//   summary.setAttribute('role', 'button');
//   summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

//   if (summary.nextElementSibling.getAttribute('id')) {
//     summary.setAttribute('aria-controls', summary.nextElementSibling.id);
//   }

//   summary.addEventListener('click', (event) => {
//     event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
//   });

//   if (summary.closest('header-drawer, menu-drawer')) return;
//   summary.parentElement.addEventListener('keyup', onKeyUpEscape);
// });

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  // Skip header inline menu because custom hover/click code handles it
  if (summary.closest('.header__inline-menu')) return;

  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling && summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details').hasAttribute('open')
    );
  });

  if (summary.closest('header-drawer, menu-drawer')) return;

  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});


// Header inline menu open on hover and click - desktop only
document.addEventListener('DOMContentLoaded', function () {
  const desktopScreen = window.matchMedia('(min-width: 990px)');

  // Only target menu inside .header__inline-menu
  const headerMenus = document.querySelectorAll(
    '.header__inline-menu header-menu > details.mega-menu'
  );

  function closeMenu(details) {
    const summary = details.querySelector('summary');

    details.removeAttribute('open');
    details.classList.remove('menu-opening');

    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
    }
  }

  function openMenu(details) {
    const summary = details.querySelector('summary');

    headerMenus.forEach(function (otherDetails) {
      if (otherDetails !== details) {
        closeMenu(otherDetails);
      }
    });

    details.setAttribute('open', '');
    details.classList.add('menu-opening');

    if (summary) {
      summary.setAttribute('aria-expanded', 'true');
    }
  }

  headerMenus.forEach(function (details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', details.hasAttribute('open'));

    if (summary.nextElementSibling && summary.nextElementSibling.getAttribute('id')) {
      summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    }

    // Hover open
    details.addEventListener('mouseenter', function () {
      if (!desktopScreen.matches) return;
      openMenu(details);
    });

    // Hover close
    details.addEventListener('mouseleave', function () {
      if (!desktopScreen.matches) return;
      closeMenu(details);
    });

    // Click open
    summary.addEventListener(
      'click',
      function (event) {
        if (!desktopScreen.matches) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        openMenu(details);
      },
      true
    );

    details.addEventListener('keyup', onKeyUpEscape);
  });
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

// // Collection JS
// document.addEventListener('DOMContentLoaded', () => {
//   const gridBtn = document.querySelector('.collection-grid-sort-button');
//   const rowBtn = document.querySelector('.collection-row-sort-button');
//   const productGrid = document.querySelector('.product-grid-container');

//   if (!gridBtn || !rowBtn || !productGrid) return;

//   gridBtn.addEventListener('click', () => {
//     // active state
//     gridBtn.classList.add('active');
//     rowBtn.classList.remove('active');

//     // remove row view
//     productGrid.classList.remove('collection-row-view');
//   });

//   rowBtn.addEventListener('click', () => {
//     // active state
//     rowBtn.classList.add('active');
//     gridBtn.classList.remove('active');

//     // add row view
//     productGrid.classList.add('collection-row-view');
//   });
// });

// Collection JS
document.addEventListener('DOMContentLoaded', () => {
  const storageKey = `collectionViewMode:${window.location.pathname}`;

  function applyCollectionView(viewMode) {
    const gridBtn = document.querySelector('.collection-grid-sort-button');
    const rowBtn = document.querySelector('.collection-row-sort-button');
    const productGrid = document.querySelector('.product-grid-container');

    if (!gridBtn || !rowBtn || !productGrid) return;

    if (viewMode === 'row') {
      rowBtn.classList.add('active');
      gridBtn.classList.remove('active');
      productGrid.classList.add('collection-row-view');
    } else {
      gridBtn.classList.add('active');
      rowBtn.classList.remove('active');
      productGrid.classList.remove('collection-row-view');
    }
  }

  // Apply saved view on page load / pagination reload
  const savedView = localStorage.getItem(storageKey) || 'grid';
  applyCollectionView(savedView);

  // Use event delegation so it still works if Shopify reloads collection HTML
  document.addEventListener('click', (event) => {
    const gridBtn = event.target.closest('.collection-grid-sort-button');
    const rowBtn = event.target.closest('.collection-row-sort-button');

    if (gridBtn) {
      localStorage.setItem(storageKey, 'grid');
      applyCollectionView('grid');
    }

    if (rowBtn) {
      localStorage.setItem(storageKey, 'row');
      applyCollectionView('row');
    }
  });

  // Re-apply view after AJAX/filter/pagination DOM updates
  const observer = new MutationObserver(() => {
    const savedView = localStorage.getItem(storageKey) || 'grid';
    applyCollectionView(savedView);
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
});

// // Card hover image and price change
// function initCardHoverVariant() {
//   document.querySelectorAll('.card').forEach((card) => {
//     if (card.dataset.variantHoverInitialized === 'true') return;
//     card.dataset.variantHoverInitialized = 'true';

//     const mainImg = card.querySelector('.card__media .media img');
//     const priceEl = card.querySelector('.price .price-item--last');
//     const comparePriceEl = card.querySelector('.price .price__sale .price-item--regular');
//     const productLinks = card.querySelectorAll('a[href*="/products/"]');

//     const quickAddButtons = card.querySelectorAll(
//       'modal-opener button[data-product-url], modal-opener button[data-base-url]'
//     );
//     const modalOpeners = card.querySelectorAll('modal-opener[data-modal]');

//     const variantItems = card.querySelectorAll('.variant-item');
//     const mainVariantImages = card.querySelectorAll('.main-variant-image');

//     const updateProductUrl = (element, variantId) => {
//       if (!element || !variantId) return;

//       const baseUrl = element.dataset.baseUrl || element.dataset.productUrl;
//       if (!baseUrl) return;

//       const url = new URL(baseUrl, window.location.origin);
//       const newProductUrl = `${url.pathname}?variant=${variantId}`;

//       element.setAttribute('data-product-url', newProductUrl);
//       element.dataset.productUrl = newProductUrl;
//     };

//     variantItems.forEach((item, index) => {
//       const targetImg = mainVariantImages[index];
//       if (!targetImg) return;

//       const applyVariant = () => {
//         const variantId = targetImg.dataset.variantId;
//         const price = targetImg.dataset.price;
//         const comparePrice = targetImg.dataset.comparePrice;

//         if (variantId) {
//           card.dataset.selectedVariant = variantId;
//         }

//         if (mainImg) {
//           mainImg.src = targetImg.src;

//           if (targetImg.srcset) {
//             mainImg.setAttribute('srcset', targetImg.srcset);
//           } else {
//             mainImg.removeAttribute('srcset');
//           }
//           mainImg.classList.add('product-metal-img');
//         }

//         if (price && priceEl) {
//           priceEl.innerHTML = price;
//         }

//         if (comparePriceEl) {
//           if (comparePrice) {
//             comparePriceEl.innerHTML = comparePrice;
//             comparePriceEl.style.display = '';
//           } else {
//             comparePriceEl.style.display = 'none';
//           }
//         }

//         if (variantId) {
//           productLinks.forEach((link) => {
//             const url = new URL(link.href, window.location.origin);
//             url.searchParams.set('variant', variantId);
//             link.href = url.toString();
//           });

//           quickAddButtons.forEach((btn) => {
//             updateProductUrl(btn, variantId);
//           });

//           modalOpeners.forEach((opener) => {
//             const innerBtn = opener.querySelector(
//               'button[data-product-url], button[data-base-url]'
//             );
//             updateProductUrl(innerBtn, variantId);
//           });
//         }
//       };

//       item.addEventListener('mouseenter', applyVariant);
//       item.addEventListener('click', applyVariant);
//       item.addEventListener('touchstart', applyVariant, { passive: true });

//       if (index === 0) {
//         applyVariant();
//       }
//     });
//   });
// }

// document.addEventListener('DOMContentLoaded', initCardHoverVariant);

// const observer = new MutationObserver(() => {
//   initCardHoverVariant();
// });

// observer.observe(document.body, {
//   childList: true,
//   subtree: true
// });

// // Card hover image, price, and variant URL change
// function initCardHoverVariant() {
//   document.querySelectorAll('.card').forEach((card) => {
//     if (card.dataset.variantHoverInitialized === 'true') return;
//     card.dataset.variantHoverInitialized = 'true';

//     const mainImg = card.querySelector('.card__media .media img');
//     const priceWrapper = card.querySelector('.price');
//     const productLinks = card.querySelectorAll('a[href*="/products/"]');

//     const quickAddButtons = card.querySelectorAll(
//       'modal-opener button[data-product-url], modal-opener button[data-base-url]'
//     );

//     const modalOpeners = card.querySelectorAll('modal-opener[data-modal]');
//     const variantItems = card.querySelectorAll('.variant-item');
//     const mainVariantImages = card.querySelectorAll('.main-variant-image');
//     const swymButtons = card.querySelectorAll(
//       '.block__swym-add-to-wishlist-btn, .block__swym-add-to-wishli-btn'
//     );

//     function updateProductUrl(element, variantId) {
//       if (!element || !variantId) return;

//       const baseUrl = element.dataset.baseUrl || element.dataset.productUrl;
//       if (!baseUrl) return;

//       const url = new URL(baseUrl, window.location.origin);
//       const newProductUrl = `${url.pathname}?variant=${variantId}`;

//       element.setAttribute('data-product-url', newProductUrl);
//       element.dataset.productUrl = newProductUrl;
//     }
//     function updateSwymButtonId(button, variantId) {
//   if (!button || !variantId) return;

//   if (!button.dataset.baseSwymId) {
//     button.dataset.baseSwymId =
//       button.id ||
//       `swym-wishlist-${button.dataset.productId || 'product'}`;
//   }

//   button.id = `${button.dataset.baseSwymId}-${variantId}`;
//   button.setAttribute('data-variant-id', variantId);
// }

//     function updateCardPrice(price, comparePrice) {
//       if (!priceWrapper || !price) return;

//       const regularPriceItems = priceWrapper.querySelectorAll(
//         '.price__regular .price-item--regular, .price-item-sig'
//       );

//       const salePriceItems = priceWrapper.querySelectorAll(
//         '.price__sale .price-item--sale, .price__sale .price-item--last'
//       );

//       const comparePriceItems = priceWrapper.querySelectorAll(
//         '.price__sale .price-item--regular, .variant-item__old-price'
//       );

//       // Update regular price
//       regularPriceItems.forEach((priceItem) => {
//         priceItem.innerHTML = price;
//       });

//       // Update sale price also, because some themes show sale price container
//       salePriceItems.forEach((priceItem) => {
//         priceItem.innerHTML = price;
//       });

//       // Update compare-at price
//       if (comparePrice && comparePrice.trim() !== '') {
//         priceWrapper.classList.add('price--on-sale');

//         comparePriceItems.forEach((priceItem) => {
//           priceItem.innerHTML = comparePrice;
//           priceItem.style.display = '';
//         });
//       } else {
//         priceWrapper.classList.remove('price--on-sale');

//         comparePriceItems.forEach((priceItem) => {
//           priceItem.innerHTML = '';
//           priceItem.style.display = 'none';
//         });
//       }
//     }

//     variantItems.forEach((item, index) => {
//       const itemVariantId = item.dataset.variantId;
//       const targetImg =
//         card.querySelector(`.main-variant-image[data-variant-id="${itemVariantId}"]`) ||
//         mainVariantImages[index];

//       if (!targetImg) return;

//       function applyVariant() {
//         const variantId = targetImg.dataset.variantId;
//         const price = targetImg.dataset.price;
//         const comparePrice = targetImg.dataset.comparePrice;

//         if (variantId) {
//           card.dataset.selectedVariant = variantId;
//         }

//         // Update image
//         if (mainImg) {
//           mainImg.src = targetImg.src;

//           if (targetImg.srcset) {
//             mainImg.setAttribute('srcset', targetImg.srcset);
//           } else {
//             mainImg.removeAttribute('srcset');
//           }

//           mainImg.classList.add('product-metal-img');
//         }

//         // Update price
//         updateCardPrice(price, comparePrice);

//         // Update product links and quick add URLs
//         if (variantId) {
//           productLinks.forEach((link) => {
//             const url = new URL(link.href, window.location.origin);
//             url.searchParams.set('variant', variantId);
//             link.href = url.toString();
//           });

//           quickAddButtons.forEach((btn) => {
//             updateProductUrl(btn, variantId);
//           });

//           modalOpeners.forEach((opener) => {
//             const innerBtn = opener.querySelector(
//               'button[data-product-url], button[data-base-url]'
//             );
//             updateProductUrl(innerBtn, variantId);
//           });
//           swymButtons.forEach((button) => {
//   updateSwymButtonId(button, variantId);
// });
//         }
//       }

//       item.addEventListener('mouseenter', applyVariant);
//       item.addEventListener('click', applyVariant);
//       item.addEventListener('touchstart', applyVariant, { passive: true });

//       // if (index === 0) {
//       //   applyVariant();
//       // }
//       if (index === 0) {
//       applyVariant();

//       // Re-apply after app/filter price scripts finish rendering
//       setTimeout(applyVariant, 300);
//       setTimeout(applyVariant, 800);
//     }
//     });
//   });
// }

// document.addEventListener('DOMContentLoaded', initCardHoverVariant);

// const observer = new MutationObserver(() => {
//   initCardHoverVariant();
// });

// observer.observe(document.body, {
//   childList: true,
//   subtree: true
// });

// Card hover image, price, and variant URL change
function initCardHoverVariant() {
  document.querySelectorAll('.card').forEach((card) => {
    if (card.dataset.variantHoverInitialized === 'true') return;
    card.dataset.variantHoverInitialized = 'true';

    const mainImg = card.querySelector('.card__media .media img');
    const priceWrapper = card.querySelector('.price');
    const productLinks = card.querySelectorAll('a[href*="/products/"]');

    const quickAddButtons = card.querySelectorAll(
      'modal-opener button[data-product-url], modal-opener button[data-base-url]'
    );

    const modalOpeners = card.querySelectorAll('modal-opener[data-modal]');
    const variantItems = card.querySelectorAll('.variant-item');
    const mainVariantImages = card.querySelectorAll('.main-variant-image');

    function getFlitsWishlistWrappers() {
      return card.querySelectorAll('.block__flits-wishlist-card-btn');
    }

    function updateProductUrl(element, variantId) {
      if (!element || !variantId) return;

      const baseUrl = element.dataset.baseUrl || element.dataset.productUrl;
      if (!baseUrl) return;

      const url = new URL(baseUrl, window.location.origin);
      const newProductUrl = `${url.pathname}?variant=${variantId}`;

      element.setAttribute('data-product-url', newProductUrl);
      element.dataset.productUrl = newProductUrl;
    }

    function reloadFlitsButton(wrapper, variantId) {
      if (!wrapper || !variantId) return;

      const variantIdString = String(variantId);

      // Do not reload if the same variant is already applied
      if (wrapper.dataset.flitsCurrentVariantId === variantIdString) return;

      // Update variant data on the wrapper
      wrapper.setAttribute('data-variant-id', variantId);
      wrapper.dataset.variantId = variantId;
      wrapper.dataset.flitsCurrentVariantId = variantIdString;

      // Replace only the collection mount; the PDP integration is unchanged.
      const oldFlitsDiv = wrapper.querySelector('.flits_wishlist_collection_manual_button');
      if (oldFlitsDiv) {
        const newFlitsDiv = document.createElement('div');
        newFlitsDiv.className = 'flits_wishlist_collection_manual_button';
        oldFlitsDiv.replaceWith(newFlitsDiv);
      }
    }

    function updateCardPrice(price, comparePrice) {
      if (!priceWrapper || !price) return;

      const regularPriceItems = priceWrapper.querySelectorAll(
        '.price__regular .price-item--regular, .price-item-sig'
      );

      const salePriceItems = priceWrapper.querySelectorAll(
        '.price__sale .price-item--sale, .price__sale .price-item--last'
      );

      const comparePriceItems = priceWrapper.querySelectorAll(
        '.price__sale .price-item--regular, .variant-item__old-price'
      );

      // Update regular price
      regularPriceItems.forEach((priceItem) => {
        priceItem.innerHTML = price;
      });

      // Update sale price also, because some themes show sale price container
      salePriceItems.forEach((priceItem) => {
        priceItem.innerHTML = price;
      });

      // Update compare-at price
      if (comparePrice && comparePrice.trim() !== '') {
        priceWrapper.classList.add('price--on-sale');

        comparePriceItems.forEach((priceItem) => {
          priceItem.innerHTML = comparePrice;
          priceItem.style.display = '';
        });
      } else {
        priceWrapper.classList.remove('price--on-sale');

        comparePriceItems.forEach((priceItem) => {
          priceItem.innerHTML = '';
          priceItem.style.display = 'none';
        });
      }
    }

    variantItems.forEach((item, index) => {
      const itemVariantId = item.dataset.variantId;
      const targetImg =
        card.querySelector(`.main-variant-image[data-variant-id="${itemVariantId}"]`) ||
        mainVariantImages[index];

      if (!targetImg) return;

      function applyVariant() {
        const variantId = targetImg.dataset.variantId;
        const price = targetImg.dataset.price;
        const comparePrice = targetImg.dataset.comparePrice;

        if (variantId) {
          card.dataset.selectedVariant = variantId;
        }

        // Update image
        if (mainImg) {
          mainImg.src = targetImg.src;

          if (targetImg.srcset) {
            mainImg.setAttribute('srcset', targetImg.srcset);
          } else {
            mainImg.removeAttribute('srcset');
          }

          mainImg.classList.add('product-metal-img');
        }

        // Update price
        updateCardPrice(price, comparePrice);

        // Update product links and quick add URLs
        if (variantId) {
          productLinks.forEach((link) => {
            const url = new URL(link.href, window.location.origin);
            url.searchParams.set('variant', variantId);
            link.href = url.toString();
          });

          quickAddButtons.forEach((btn) => {
            updateProductUrl(btn, variantId);
          });

          modalOpeners.forEach((opener) => {
            const innerBtn = opener.querySelector(
              'button[data-product-url], button[data-base-url]'
            );
            updateProductUrl(innerBtn, variantId);
          });

          // Reload Flits wishlist button after updating variant value
          getFlitsWishlistWrappers().forEach((wrapper) => {
            reloadFlitsButton(wrapper, variantId);
          });
        }
      }

      item.addEventListener('mouseenter', applyVariant);
      item.addEventListener('click', applyVariant);
      item.addEventListener('touchstart', applyVariant, { passive: true });

      // if (index === 0) {
      //   applyVariant();
      // }
      if (index === 0) {
        applyVariant();

        // Re-apply after app/filter price scripts finish rendering
        setTimeout(applyVariant, 300);
        setTimeout(applyVariant, 800);
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', initCardHoverVariant);

const observer = new MutationObserver(() => {
  initCardHoverVariant();
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});

// PDP People Viewing
  document.addEventListener('DOMContentLoaded', function () {
  const peopleCountEl = document.querySelector('.people-count__number');
  if (!peopleCountEl) return;

  const storageKey = 'people_viewing_count_' + window.location.pathname;
  let count = sessionStorage.getItem(storageKey);

  if (!count) {
    const min = 3;
    const max = 18;
    count = Math.floor(Math.random() * (max - min + 1)) + min;
    sessionStorage.setItem(storageKey, count);
  }

  peopleCountEl.textContent = count;
});

// PDP Drop Down Accordion
function initCustomAccordions() {
  const accordions = document.querySelectorAll('.accordion-wrapper');

  const setContentBaseStyles = (content) => {
    content.style.overflow = 'hidden';
    content.style.transition = 'max-height 0.4s ease';
  };

  const openAccordion = (wrapper, content) => {
    wrapper.classList.add('is-open');
    content.classList.add('is-open');

    content.style.maxHeight = '0px';

    requestAnimationFrame(() => {
      content.style.maxHeight = `${content.scrollHeight}px`;
    });

    content.addEventListener(
      'transitionend',
      () => {
        if (wrapper.classList.contains('is-open')) {
          content.style.maxHeight = 'none';
        }
      },
      { once: true }
    );
  };

  const closeAccordion = (wrapper, content) => {
    content.style.maxHeight = `${content.scrollHeight}px`;

    requestAnimationFrame(() => {
      content.style.maxHeight = '0px';
    });

    wrapper.classList.remove('is-open');
    content.classList.remove('is-open');
  };

  const closeOtherAccordions = (currentWrapper) => {
    document.querySelectorAll('.accordion-wrapper.is-open').forEach((wrapper) => {
      if (wrapper === currentWrapper) return;

      const content = wrapper.querySelector('.accordion-content');
      if (!content) return;

      closeAccordion(wrapper, content);
    });
  };

  const keepTitlePosition = (title, callback) => {
    const titleTopBefore = title.getBoundingClientRect().top;

    callback();

    requestAnimationFrame(() => {
      const titleTopAfter = title.getBoundingClientRect().top;
      const scrollDiff = titleTopAfter - titleTopBefore;

      if (scrollDiff !== 0) {
        window.scrollBy(0, scrollDiff);
      }
    });
  };

  accordions.forEach((wrapper) => {
    if (wrapper.dataset.accordionInitialized === 'true') return;

    const title = wrapper.querySelector('.accordion-title');
    const content = wrapper.querySelector('.accordion-content');

    if (!title || !content) return;

    wrapper.dataset.accordionInitialized = 'true';

    setContentBaseStyles(content);

    if (wrapper.classList.contains('is-open')) {
      content.style.maxHeight = `${content.scrollHeight}px`;

      content.addEventListener(
        'transitionend',
        () => {
          if (wrapper.classList.contains('is-open')) {
            content.style.maxHeight = 'none';
          }
        },
        { once: true }
      );
    } else {
      content.style.maxHeight = '0px';
    }

    title.addEventListener('click', () => {
      keepTitlePosition(title, () => {
        const isOpen = wrapper.classList.contains('is-open');

        closeOtherAccordions(wrapper);

        if (isOpen) {
          closeAccordion(wrapper, content);
        } else {
          openAccordion(wrapper, content);
        }
      });
    });
  });
}

document.addEventListener('DOMContentLoaded', initCustomAccordions);
// Initialize product gallery in quick view popup

function initializeProductGallery(isColorSwitchCall = false) {
  // Clear the slide elements cache if this is not a color switch call, because the DOM has updated
  if (!isColorSwitchCall) {
    window.originalMainSlides = null;
    window.originalThumbSlides = null;
  }

  // Find the selected color and filter the slides in the DOM before initializing Swiper
  const checkedColorInput = document.querySelector('input[name="meta_color_selection"]:checked');
  if (checkedColorInput) {
    const selectedColorHandle = checkedColorInput.getAttribute('data-color-handle');

    // Determine the selected size group (e.g., "Threaded - 5.5mm" → "threaded")
    let selectedSizeGroup = '';
    const sizeDropdown = document.querySelector('.custom-product-dropdown') || document.querySelector('select[name="Size"]') || document.querySelector('select[name="SIZE"]');
    if (sizeDropdown && sizeDropdown.value) {
      const sizeVal = sizeDropdown.value.trim();
      if (sizeVal.includes(' - ')) {
        selectedSizeGroup = sizeVal.split(' - ')[0].trim().toLowerCase();
      } else {
        selectedSizeGroup = sizeVal.trim().toLowerCase();
      }
    }

    // Helper: check if a slide passes the size filter
    // Slides WITHOUT data-media-size always pass (backward compatible)
    // Slides WITH data-media-size only pass if it matches the selected size group
    function passesSizeFilter(element) {
      const mediaSize = element.getAttribute('data-media-size');
      if (!mediaSize) return true; // no size tag = always show
      if (!selectedSizeGroup) return true; // no size selected = show all
      return mediaSize.trim().toLowerCase() === selectedSizeGroup;
    }
    
    // Cache slide elements if not already cached
    if (!window.originalMainSlides) {
      const mainSlides = document.querySelectorAll('.block_top-slider-main .block_top-image-main > .swiper-slide');
      window.originalMainSlides = Array.from(mainSlides);
    }
    if (!window.originalThumbSlides) {
      const thumbSlides = document.querySelectorAll('.block_top-slider-thumb .swiper-wrapper > .swiper-slide');
      window.originalThumbSlides = Array.from(thumbSlides);
    }

    // Filter main slider slides in the DOM (by color + size)
    const mainWrapper = document.querySelector('.block_top-slider-main .block_top-image-main');
    if (mainWrapper && window.originalMainSlides.length > 0) {
      mainWrapper.innerHTML = '';
      window.originalMainSlides.forEach(slide => {
        if (slide.getAttribute('data-media-color') === selectedColorHandle && passesSizeFilter(slide)) {
          mainWrapper.appendChild(slide.cloneNode(true));
        }
      });
    }

    // Filter thumb slider slides in the DOM (by color + size)
    const thumbWrapper = document.querySelector('.block_top-slider-thumb .swiper-wrapper');
    if (thumbWrapper && window.originalThumbSlides.length > 0) {
      thumbWrapper.innerHTML = '';
      window.originalThumbSlides.forEach(slide => {
        if (slide.getAttribute('data-media-color') === selectedColorHandle && passesSizeFilter(slide)) {
          thumbWrapper.appendChild(slide.cloneNode(true));
        }
      });
    }

    // Filter bottom model images and videos
    const bottomWrapper = document.querySelector('.block_product-varient-images');
    if (bottomWrapper) {
      const bottomMediaItems = bottomWrapper.querySelectorAll('.block_product-varient-images-info');
      let visibleCount = 0;
      let visibleIndex = 0;
      bottomMediaItems.forEach(item => {
        const itemColor = item.getAttribute('data-media-color');
        if (itemColor === selectedColorHandle) {
          item.style.display = 'block';
          visibleCount++;
        } else {
          item.style.display = 'none';
        }
      });
      bottomMediaItems.forEach(item => {
        if (item.style.display !== 'none') {
          item.classList.remove('block_product-varient-single-image-item', 'block_product-varient-right');
          if (visibleCount === 1) {
            item.classList.add('block_product-varient-single-image-item');
          } else if (visibleIndex === 1 || visibleIndex === 2) {
            item.classList.add('block_product-varient-right');
          }
          visibleIndex++;
        }
      });
    }
  }

  const thumbsEl = document.querySelector(".block_top-slider-thumb");
  const mainEl = document.querySelector(".block_top-slider-main");

  if (thumbsEl && mainEl) {
    const totalThumbs = thumbsEl.querySelectorAll(".swiper-slide").length;

    // Initialize thumbs
    window.swiperThumbsInstance = new Swiper(thumbsEl, {
      spaceBetween: 0,
      slidesPerView: totalThumbs,
      freeMode: true,
      watchSlidesProgress: true,
    });

    // Initialize main slider
    window.swiperMainInstance = new Swiper(mainEl, {
      loop: true,
      slidesPerView: 1.2, // mobile <= 740px
      spaceBetween: 10,
      watchSlidesProgress: true,

      breakpoints: {
        0: {
          slidesPerView: 1,
          spaceBetween: 10,
        },
        741: {
          slidesPerView: 1,
          spaceBetween: 10,
        },
      },

      navigation: {
        nextEl: ".block_top-slider-right-arrow",
        prevEl: ".block_top-slider-left-arrow",
      },

      thumbs: {
        swiper: window.swiperThumbsInstance,
      },

      pagination: {
        el: ".swiper-pagination",
        type: "progressbar",
      },
    });
  } else {
    console.warn("Swiper elements not found on the page.");
  }
}


// PDP POP-UP JS
document.addEventListener('DOMContentLoaded', () => {
  /* CHECK POPUP HAS DATA */
  const hasPopupData = () => {
    const popup = document.querySelector('.block__ring_size_chart-pop-up');

    if (!popup) return false;

    const popupContent = popup.querySelector('.block__ring_size_chart-content');

    // If you have specific content wrapper, check that
    if (popupContent && popupContent.textContent.trim() !== '') {
      return true;
    }

    // Fallback check
    return popup.textContent.trim() !== '';
  };

  /* CLOSE FUNCTION */
  const closePopup = () => {
    const popup = document.querySelector('.block__ring_size_chart-pop-up');

    popup?.classList.remove('active');
    document.body.style.overflow = '';
  };

  /* OPEN / CLOSE POPUP */
  document.addEventListener('click', (e) => {
    const target = e.target;

    if (!(target instanceof Element)) return;

    if (target.closest('.size-guide-wrapper')) {
      e.preventDefault();

      const popup = document.querySelector('.block__ring_size_chart-pop-up');

      // If popup does not exist or has no data, do not add active class or body overflow
      if (!popup || !hasPopupData()) return;

      popup.classList.add('active');
      document.body.style.overflow = 'hidden';
    }

    if (
      target.closest('.block__ring_size_chart-close') ||
      target.closest('.block__ring_size_chart-overlay')
    ) {
      closePopup();
    }
  });

  /* ESC CLOSE */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closePopup();
  });

  /* TABLE GRADIENT */
  const checkScroll = () => {
    const tableWrap = document.querySelector('.block__ring_size_chart-table-wrap');
    const rightWrap = document.querySelector('.block__ring_size_chart-right');

    if (!tableWrap || !rightWrap) return;

    const isBottom =
      tableWrap.scrollTop + tableWrap.clientHeight >= tableWrap.scrollHeight - 2;

    const noScroll =
      tableWrap.scrollHeight <= tableWrap.clientHeight;

    rightWrap.classList.toggle('hide-gradient', isBottom || noScroll);
  };

  checkScroll();

  document.addEventListener(
    'scroll',
    (e) => {
      const target = e.target;

      if (!(target instanceof Element)) return;

      if (target.closest('.block__ring_size_chart-table-wrap')) {
        checkScroll();
      }
    },
    true
  );

  window.addEventListener('resize', checkScroll);
});

// Header mega menu slider

document.addEventListener('DOMContentLoaded', function () {

  document.querySelectorAll('.mega-menu__content-wrapper').forEach(function(slider) {

    new Swiper(slider, {
      slidesPerView: 5,
      spaceBetween: 30,
      speed: 600,
      loop: false,
      watchOverflow: true,
      observer: true,
      observeParents: true,

      navigation: {
        nextEl: slider.querySelector('.swiper-button-next'),
        prevEl: slider.querySelector('.swiper-button-prev'),
      },

      breakpoints: {
        320: {
          slidesPerView: 1.2,
          spaceBetween: 15
        },
        768: {
          slidesPerView: 3,
          spaceBetween: 20
        },
        1190: {
          slidesPerView: 4,
          spaceBetween: 24
        }
      }
    });

  });

});

// Footer mobile menu accordion
document.addEventListener('DOMContentLoaded', function() {
  const headings = document.querySelectorAll('.footer-block__heading');

  headings.forEach((heading) => {
    heading.addEventListener('click', function() {
      const parentMenu = this.closest('.footer-block--menu');
      const isActive = parentMenu.classList.contains('active');

      document.querySelectorAll('.footer-block--menu.active').forEach((menu) => {
        menu.classList.remove('active');
      });

      if (!isActive) {
        parentMenu.classList.add('active');
      }
    });
  });
});

// PDP Meta Color & Size selection handler
document.addEventListener('change', (event) => {
  if (event.target && (event.target.name === 'meta_color_selection' || event.target.classList.contains('custom-product-dropdown') || event.target.name === 'Size' || event.target.name === 'SIZE')) {
    if (event.target.name === 'meta_color_selection') {
      const selectedValue = event.target.value;
      
      // Update hidden input properties[Metal Color] value
      const hiddenInput = document.getElementById('Selected-Meta-Color-Input');
      if (hiddenInput) {
        hiddenInput.value = selectedValue;
      }
      
      // Update legend selected color label
      const selectedLabel = document.getElementById('selected-meta-color-label');
      if (selectedLabel) {
        selectedLabel.innerText = selectedValue;
      }
    }

    // Destroy existing Swiper instances first to avoid layout glitches
    if (typeof window.swiperMainInstance?.destroy === 'function') {
      window.swiperMainInstance.destroy(true, true);
      window.swiperMainInstance = null;
    }
    if (typeof window.swiperThumbsInstance?.destroy === 'function') {
      window.swiperThumbsInstance.destroy(true, true);
      window.swiperThumbsInstance = null;
    }

    // Call initializeProductGallery with isColorSwitchCall = true
    initializeProductGallery(true);

    // Re-bind fancybox
    if (window.jQuery && typeof window.jQuery.fancybox === 'function') {
      window.jQuery('[data-fancybox]').fancybox();
    }
  }
});

// ATC shake animation – every 4 seconds
(function () {
  const atcBtn = document.querySelector('.product-form__submit');
  if (!atcBtn) return;
  setInterval(function () {
    atcBtn.classList.add('is--animated', 't4s-ani-shake');
    atcBtn.addEventListener('animationend', function handler() {
      atcBtn.classList.remove('is--animated', 't4s-ani-shake');
      atcBtn.removeEventListener('animationend', handler);
    });
  }, 4000);
})();

// lab-grown-diamond-guide
document.addEventListener('DOMContentLoaded', function () {
  const modal = document.getElementById('lab-grown-diamond-guide-modal');

  // If there is no matching guide/modal,
  // remove all Lab Grown Diamond Guide triggers.
  if (!modal) {
    document.querySelectorAll('.block__lab-grown-diamond-guide').forEach(function (trigger) {
      trigger.remove();
    });
  }
});


document.addEventListener('click', function (event) {
  const trigger = event.target.closest('.block__lab-grown-diamond-guide');

  if (trigger) {
    event.preventDefault();

    const modal = document.getElementById('lab-grown-diamond-guide-modal');

    if (!modal) {
      trigger.remove();
      return;
    }

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');

    document.body.classList.add('lab-grown-diamond-guide-open');

    return;
  }


  const closeButton = event.target.closest(
    '.lab-grown-diamond-guide-close'
  );

  if (closeButton) {
    closeLabGrownDiamondGuide();
    return;
  }


  const overlay = event.target.closest(
    '.lab-grown-diamond-guide-overlay'
  );

  if (overlay) {
    closeLabGrownDiamondGuide();
  }
});


function closeLabGrownDiamondGuide() {
  const modal = document.getElementById(
    'lab-grown-diamond-guide-modal'
  );

  if (!modal) return;

  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');

  document.body.classList.remove(
    'lab-grown-diamond-guide-open'
  );
}


document.addEventListener('keydown', function (event) {
  if (event.key !== 'Escape') return;

  const modal = document.getElementById(
    'lab-grown-diamond-guide-modal'
  );

  if (!modal || !modal.classList.contains('is-open')) return;

  closeLabGrownDiamondGuide();
});  

// PDP Copy offer code
document.addEventListener('click', async (event) => {
  const button = event.target.closest(
    '.block__product-offer-sec [data-offer-code]'
  );

  if (!button) return;

  try {
    await navigator.clipboard.writeText(button.dataset.offerCode);

    button.setAttribute('aria-label', 'Offer code copied');
    button.title = 'Copied!';
  } catch (error) {
    console.error('Unable to copy offer code:', error);
  }
}); 
// Native offer carousel: delegated handlers also support product section replacements.
(() => {
  const selector = '.block__product-offer-sec__slider';
  let drag = null;
  let suppressClick = null;

  function advance(slider, direction) {
    const cards = slider.querySelectorAll('.block__product-offer-sec__card');
    const distance = cards.length > 1
      ? Math.abs(cards[1].offsetLeft - cards[0].offsetLeft)
      : slider.clientWidth;
    slider.scrollBy({ left: direction * distance,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  document.addEventListener('click', event => {
    if (suppressClick && suppressClick.contains(event.target)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressClick = null;
      return;
    }
    const button = event.target.closest('[data-offer-direction]');
    if (!button) return;
    const slider = button.closest('.block__product-offer-sec')?.querySelector(selector);
    if (slider) advance(slider, Number(button.dataset.offerDirection));
  }, true);

  document.addEventListener('keydown', event => {
    if (!event.target.matches(selector) || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    advance(event.target, event.key === 'ArrowRight' ? 1 : -1);
  });

  document.addEventListener('pointerdown', event => {
    const slider = event.target.closest(selector);
    // Touch and pen keep the browser's native scrolling and vertical page gestures.
    if (!slider || event.pointerType !== 'mouse' || event.button !== 0 ||
        event.target.closest('button, a, input, select, textarea')) return;
    suppressClick = null;
    drag = { slider, id: event.pointerId, x: event.clientX, left: slider.scrollLeft, moved: false };
  });

  document.addEventListener('pointermove', event => {
    if (!drag || event.pointerId !== drag.id) return;
    const delta = event.clientX - drag.x;
    if (!drag.moved && Math.abs(delta) < 5) return;
    if (!drag.moved) {
      drag.moved = true;
      drag.slider.classList.add('is-dragging');
      drag.slider.setPointerCapture(event.pointerId);
    }
    event.preventDefault();
    drag.slider.scrollLeft = drag.left - delta;
  });

  function finish(event) {
    if (!drag || event.pointerId !== drag.id) return;
    const { slider, moved, id } = drag;
    drag = null;
    slider.classList.remove('is-dragging');
    if (slider.hasPointerCapture(id)) slider.releasePointerCapture(id);
    if (moved) {
      suppressClick = slider;
      setTimeout(() => { if (suppressClick === slider) suppressClick = null; }, 0);
    }
  }
  document.addEventListener('pointerup', finish);
  document.addEventListener('pointercancel', finish);
  document.addEventListener('lostpointercapture', finish);
})();
 
// Initialize Flits card buttons after the SDK and after dynamic card replacements.
(() => {
  const selector = '.block__flits-wishlist-card-btn .flits_wishlist_collection_manual_button';
  let scheduled;
  let initializing = false;
  let pending = false;
  let attempts = 0;
  let recovery;
  let searchIntegration;

  async function initializeCardWishlist() {
    if (initializing) {
      pending = true;
      return;
    }
    const buttons = [...document.querySelectorAll(selector)];
    if (!buttons.some(mount => !mount.querySelector('.flits_wishlist_collection_button'))) return;

    const sdk = window.wishlistSDKInstance;
    let integration = sdk?.getFeature?.('collectionPage');

    // Search uses collection card mounts, but Flits may load the collection
    // module without registering its feature on this page type.
    if (!integration && document.body.classList.contains('block__search') &&
        sdk?.isInitialized?.() && window.FlitsWishlistCollectionPageIntegration) {
      initializing = true;
      try {
        searchIntegration ||= (async () => {
          const feature = new window.FlitsWishlistCollectionPageIntegration(sdk);
          await feature.init({
            autoInject: false,
            watchForChanges: false,
            targetSelector: '#ProductGridContainer'
          });
          return feature;
        })();
        integration = await searchIntegration;
      } catch (error) {
        searchIntegration = undefined;
        console.error('Unable to initialize Flits search wishlist:', error);
      } finally {
        initializing = false;
      }
    }

    // The store's custom collection bundle can fail before exporting its class.
    // Recover with the default module supplied by the active Flits asset loader.
    if (!integration && sdk?.isInitialized?.() && attempts >= 4 &&
        !window.FlitsWishlistCollectionPageIntegration &&
        window.FlitsWishlistConfig?.getDefaultAssetUrl) {
      recovery ||= (async () => {
        const url = window.FlitsWishlistConfig.getDefaultAssetUrl(
          'flits-wishlist-feature-collection-page.v1.0.0.min.js'
        );
        if (!url) throw new Error('Flits collection module URL is unavailable.');
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = url;
          script.onload = resolve;
          script.onerror = () => reject(new Error('Unable to load Flits collection module.'));
          document.head.appendChild(script);
        });
        let feature = sdk.getFeature('collectionPage');
        if (!feature) {
          feature = new window.FlitsWishlistCollectionPageIntegration(sdk);
          await feature.init({
            autoInject: true,
            watchForChanges: true,
            targetSelector: 'body'
          });
        }
        return feature;
      })();
      try {
        integration = await recovery;
      } catch (error) {
        console.error('Unable to recover Flits collection wishlist:', error);
        return;
      }
    }
    const injector = integration?.injector;
    if (!injector || typeof injector.injectWishlistButtons !== 'function') {
      if (attempts++ < 40) scheduled = setTimeout(initializeCardWishlist, 500);
      return;
    }


    // *search* exclusion for the theme's full search results section.
    if (integration.configManager?.getPlacement?.() === 'manual' &&
        !injector.ratnamManualMounts && typeof injector.findAllCardLinkPairs === 'function') {
      const findCardLinkPairs = injector.findAllCardLinkPairs.bind(injector);
      injector.findAllCardLinkPairs = async function (targetSelector) {
        const pairs = await findCardLinkPairs(targetSelector);
        const targets = [...document.querySelectorAll(targetSelector || 'body')];
        const manualPairs = [...document.querySelectorAll('.main-product-card .block__flits-wishlist-card-btn')]
          .filter(wrapper => targets.some(target => target.contains(wrapper)))
          .map(wrapper => ({
            card: wrapper,
            link: wrapper.closest('.main-product-card')?.querySelector('a[href*="/products/"]')
          }))
          .filter(pair => pair.link);
        return [
          ...pairs.filter(pair => !manualPairs.some(manual => pair.card.contains(manual.card))),
          ...manualPairs
        ];
      };
      injector.ratnamManualMounts = true;
    }

    initializing = true;
    try {
      // Use the entire document: featured and recommended cards can be outside
      // the collection grid chosen by Flits' automatic theme detection.
      const products = [...document.querySelectorAll('.block__flits-wishlist-card-btn')].map(wrapper => {
        const url = new URL(wrapper.dataset.productUrl, window.location.origin);
        return {
          id: wrapper.dataset.productId,
          variantId: wrapper.dataset.variantId,
          handle: url.pathname.split('/products/')[1]?.split('/')[0],
          url: url.href
        };
      });
      await injector.injectWishlistButtons(products, 'body');
    } catch (error) {
      console.error('Unable to initialize Flits card wishlist:', error);
    } finally {
      initializing = false;
      if (pending) {
        pending = false;
        schedule();
      }
    }
  }

  function schedule() {
    clearTimeout(scheduled);
    scheduled = setTimeout(initializeCardWishlist, 150);
  }

  function start() {
    schedule();
    new MutationObserver(mutations => {
      if (mutations.some(mutation => [...mutation.addedNodes].some(node =>
        node instanceof Element &&
        (node.matches(selector) || node.querySelector(selector))
      ))) schedule();
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('wishlistSDKReady', () => {
    attempts = 0;
    schedule();
  });
  document.addEventListener('flitsWishlistModulesLoaded', schedule);
  document.addEventListener('shopify:section:load', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();